import { createContext, useContext, useMemo, type ReactNode } from 'react';
import type { ApiClient } from '@mrrmlab/api-client';

export type SourceChangeKind = 'created' | 'updated' | 'deleted';

// Fired from each mutation hook after the server-side write succeeds, so the
// host app can refresh derived state (semantic-search index on mobile, future
// websocket/SSE listeners on web, etc.). `sourceType` is the entity-class the
// mutation touched — feature-lists currently emits only `'list'`; routing to
// shopping-list vs notes data sources happens in the consumer.
export interface SourceChangeEvent {
  sourceType: 'list';
  sourceId: string;
  kind: SourceChangeKind;
}

export type OnSourceChange = (event: SourceChangeEvent) => void;

interface ContextValue {
  client: ApiClient;
  onSourceChange: OnSourceChange;
}

const noopOnSourceChange: OnSourceChange = () => {};

const ApiClientContext = createContext<ContextValue | null>(null);

export interface ApiClientProviderProps {
  client: ApiClient;
  onSourceChange?: OnSourceChange;
  children: ReactNode;
}

/**
 * Provider for the generic lists feature. Independent from
 * `@mrrmlab/feature-shopping-list`'s provider so apps can mount either (or both)
 * without cross-package coupling — the same ApiClient instance can back both.
 */
export function ListsApiClientProvider({ client, onSourceChange, children }: ApiClientProviderProps) {
  const value = useMemo<ContextValue>(
    () => ({ client, onSourceChange: onSourceChange ?? noopOnSourceChange }),
    [client, onSourceChange],
  );
  return <ApiClientContext.Provider value={value}>{children}</ApiClientContext.Provider>;
}

export function useApiClient(): ApiClient {
  const ctx = useContext(ApiClientContext);
  if (!ctx) {
    throw new Error(
      '[feature-lists] useApiClient called outside <ListsApiClientProvider>. ' +
        'Wrap your app with <ListsApiClientProvider client={...}> once at the root.',
    );
  }
  return ctx.client;
}

export function useOnSourceChange(): OnSourceChange {
  const ctx = useContext(ApiClientContext);
  return ctx?.onSourceChange ?? noopOnSourceChange;
}
